import { Link, type LinkOptions } from "@tiptap/extension-link";
import { Plugin, PluginKey } from "@tiptap/pm/state";

export interface LinkClickOptions extends LinkOptions {
  /**
   * Callback when Cmd+click (Mac) or Ctrl+click (Windows/Linux) on a link.
   * Return true if the click was handled, false to allow default behavior.
   */
  onCmdClick?: (href: string, event: MouseEvent) => boolean;
  /**
   * Callback when regular click on a link.
   * Return true if the click was handled, false to allow default behavior.
   */
  onRegularClick?: (href: string, event: MouseEvent) => boolean;
}

/**
 * Custom Link extension that handles both regular clicks and Cmd+click (Mac) / Ctrl+click (Windows) to open links.
 *
 * Usage:
 * ```ts
 * LinkWithCmdClick.configure({
 *   openOnClick: false,
 *   onRegularClick: (href) => {
 *     // Handle regular click
 *     return true;
 *   },
 *   onCmdClick: (href) => {
 *     window.open(href, '_blank');
 *     return true;
 *   },
 * })
 * ```
 */
export const LinkWithCmdClick = Link.extend<LinkClickOptions>({
  addOptions() {
    const parentOptions = this.parent?.() || {};
    return {
      ...parentOptions,
      onCmdClick: undefined,
      onRegularClick: undefined,
    } as LinkClickOptions;
  },

  addProseMirrorPlugins() {
    const parentPlugins = this.parent?.() || [];
    const { onCmdClick, onRegularClick } = this.options;

    // If no callbacks provided, just use parent plugins
    if (!onCmdClick && !onRegularClick) {
      return parentPlugins;
    }

    const clickPlugin = new Plugin({
      key: new PluginKey("linkClickHandler"),
      props: {
        handleClick: (_view, _pos, event) => {
          // Find the clicked link element
          let target = event.target as HTMLElement | null;
          while (target !== null && target.nodeName !== "A" && target.nodeName !== "DIV") {
            target = target.parentNode as HTMLElement | null;
          }

          if (target?.nodeName !== "A") {
            return false;
          }

          const href = target.getAttribute("href");
          if (!href) {
            return false;
          }

          // Check for Cmd (Mac) or Ctrl (Windows/Linux) key
          const isCmdOrCtrlClick = event.metaKey || event.ctrlKey;

          let handled = false;

          if (isCmdOrCtrlClick && onCmdClick) {
            // Call the cmd+click callback
            handled = onCmdClick(href, event);
          } else if (!isCmdOrCtrlClick && onRegularClick) {
            // Call the regular click callback
            handled = onRegularClick(href, event);
          }

          if (handled) {
            event.preventDefault();
            event.stopPropagation();
          }

          return handled;
        },
      },
    });

    return [...parentPlugins, clickPlugin];
  },
});

export default LinkWithCmdClick;
